<template>
	<article class="pet-walking">
		<!-- ローディング -->
		<transition name="fade">
			<aside v-if="isLoading" class="pet-walking__loading">
				<webview-redraw-progress />
			</aside>
		</transition>
		<!-- /ローディング -->

		<!-- 日付バー -->
		<h1 class="pet-walking__title"><common-date-header :targetDate="targetDate" @reload="init" /></h1>
		<!-- /日付バー -->

		<!-- サマリー -->
		<section class="pet-walking__summary">
			<h2 class="pet-walking__summary--title">散歩</h2>
			<section class="pet-walking__summary--contents">
				<h3>距離</h3>
				<p v-show ="displayType === 'day'">合計<em>{{ computedSummaryData.total_distance }}</em>km</p>
				<p v-show ="displayType !== 'day'">平均<em>{{ computedSummaryData.average_distance }}</em>km</p>
			</section>
			<section class="pet-walking__summary--contents">
				<h3>経過時間</h3>
				<p v-show ="displayType === 'day'">合計<em>{{ computedSummaryData.total_duration }}</em>分</p>
				<p v-show ="displayType !== 'day'">平均<em>{{ computedSummaryData.average_duration }}</em>分</p>
			</section>
			<svg @click="openModal('walking')" width="16" height="16" viewBox="0 0 16 16" fill="none">
				<g clip-path="url(#clip0_1113_25126)">
					<path d="M15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5C12.1421 0.5 15.5 3.85786 15.5 8Z" fill="white" stroke="#1EBFBB"/>
					<path d="M7.37158 4.58957V3.19858H8.76258V4.58957H7.37158ZM7.37158 12.6672V6.10061H8.76258V12.6672H7.37158Z" fill="#1EBFBB"/>
				</g>
				<defs>
					<clipPath id="clip0_1113_25126">
						<rect width="16" height="16" fill="white"/>
					</clipPath>
				</defs>
			</svg>
		</section>
		<!-- /サマリー -->

		<!-- 散歩レポート-->
		<div class="pet-walking__inner">
			<!-- 距離チャート -->
			<section class="pet-walking__contents">
				<h2 class="pet-walking__subtitle">距離</h2>
				<bar-chart
					:labels="computedChartData.distance_labels"
					:datapoints="computedChartData.distance_data"
					:chartIndex="computedChartData.distance_key"
					:chartType="'walking-dustance-log'"
					:displayType="displayType"
					:xUnit="'(km)'"
					:key="`distance-chart-${computedChartData.distance_key}`"
				/>
			</section>
			<!-- /距離チャート -->

			<!-- 経過時間チャート -->
			<section class="pet-walking__contents">
				<h2 class="pet-walking__subtitle">経過時間</h2>
				<bar-chart
					:labels="computedChartData.duration_labels"
					:datapoints="computedChartData.duration_data"
					:chartIndex="computedChartData.duration_key"
					:chartType="'walking-duration-log'"
					:displayType="displayType"
					:xUnit="'(分)'"
					:key="`distance-chart-${computedChartData.duration_key}`"
				/>
			</section>
			<!-- /経過時間チャート -->

			<!-- 月変更UI -->
			<aside v-if="displayType === 'year'" class="pet-walking__month">
				<select class="pet-walking__month--select" v-model="selectedMonth" @change="changeMonth">
					<option
						v-for="(item, index) in monthList"
						:key="`walking-date-${index}`"
						:value="item.value"
					>{{ item.text }}</option>
				</select>

				<!-- 月サマリー -->
				<section class="pet-walking__summary">
					<h2 class="pet-walking__summary--title">散歩</h2>
					<section class="pet-walking__summary--contents">
						<h3>距離</h3>
						<p>平均<em>{{ computedSummaryMonthlyData.average_distance }}</em>km</p>
					</section>
					<section class="pet-walking__summary--contents">
						<h3>経過時間</h3>
						<p>平均<em>{{ computedSummaryMonthlyData.average_duration }}</em>分</p>
					</section>
				</section>
				<!-- /月サマリー -->
			</aside>
			<!-- /月変更UI -->

			<!-- 散歩ログ -->
			<section class="pet-walking__contents is-walking-log">
				<h2 class="pet-walking__subtitle">散歩ログ</h2>

				<!-- 日付変更UI -->
				<aside v-show="displayType !== 'day'" class="pet-walking__select-date swiper-container" id="js_swiper">
					<ul class="pet-walking__select-date--inner swiper-wrapper">
						<li
							v-for="(item, index) in logDateList"
							:key="`walking-date-${index}`"
							:class="[
								'pet-walking__select-date--item',
								'swiper-slide',
								{ 'is-select': item.value === logDate },
								{ 'is-empty': item.is_empty }
							]"
							@click="setlogDate(item.value)"
						>
							<p class="is-date">{{ item.date }}</p>
							<p class="is-weekday">{{ item.weekday }}</p>
						</li>
					</ul>
				</aside>
				<!-- /日付変更UI -->

				<!-- マップ -->
				<div v-if="computedlogData.length > 0" class="pet-walking__map--wrapper">
					<!-- マップ用ローディング -->
					<transition name="fade">
						<aside v-if="isMapLoading" class="pet-walking__loading">
							<webview-redraw-progress />
						</aside>
					</transition>
					<!-- /マップ用ローディング -->

					<div v-for="(item, index) in computedlogData" :key="`map-${index}`" class="pet-walking__map">
						<leaflet-map :coordinate="item.coordinate" :index="item.key" :errorMessage="item.mapErrorMessage" :key="`map-view-${index}${item.key}`" />
						<div class="pet-walking__map--summary">
							<section class="is-distance">
								<h3>距離</h3>
								<p>{{ item.distance }}km</p>
							</section>
							<section class="is-minites">
								<h3>経過時間</h3>
								<p>{{ item.elapsedMinutes }}分</p>
							</section>
							<section class="is-duration">
								<h3>{{ item.date }}</h3>
								<p>{{ item.duration.start }}～{{ item.duration.end }}</p>
							</section>
						</div>
					</div>
				</div>
				<div v-else class="pet-walking__map--error">
					<p>散歩ログがありません。</p>
				</div>
				<!-- /マップ -->
			</section>
			<!-- /散歩ログ -->

			<aside @click="openModal('data-quality')"  class="pet-walking__data-quality">健康データの精度について</aside>
		</div>
		<!-- /散歩レポート-->

		<!-- お知らせ詳細モーダル -->
		<transition name="fade">
			<aside v-if="isVisibleModal" class="pet-walking__modal">
				<div class="pet-walking__modal--inner">
					<h2 class="pet-walking__modal--title">
						<svg @click="isVisibleModal = false" width="40" height="40" viewBox="0 0 40 40" fill="none">
							<path d="M9 9.72003L9.69862 9.02141L30.9995 30.2799L30.3009 30.9785L9 9.72003Z" fill="white"/>
							<path d="M9 30.324L30.3009 9.06558L30.9995 9.7642L9.69862 31.0227L9 30.324Z" fill="white"/>
						</svg>
						<span>{{ modalType === 'walking' ? '散歩' : '健康データの精度' }}について</span>
					</h2>
					<div class="pet-walking__modal--body">
						<p v-if="modalType === 'walking'">
							散歩時間や距離を記録し、過去の記録と比較することで運動量の変化に気付いてあげることができます。<br>
							高齢になると運動量は低下する傾向にありますが、それ以外にも運動量低下の原因となる疾患が隠れている可能性もあります。<br>
							歩き方や呼吸など普段と変化がないか注意深く観察することで、病気の早期発見につながります。
						</p>
						<p v-else>
							個体差や環境等の条件により健康データの精度が悪くなることがあります。<br>
							PetVoice開発メンバーのポメラニアンのピケちゃんを筆頭に、安定して高い精度の健康データを表示できるよう鋭意改善しております。
						</p>
					</div>
				</div>
			</aside>
		</transition>
		<!-- /お知らせ詳細モーダル -->
	</article>
</template>

<script>
// Vuex
import { mapActions, mapGetters } from 'vuex'
import { SET_PET_WALKING_SUMMARY_DATA, SET_PET_WALKING_HISTORY_DATA } from '@/store/modules/pet/mutation-types'

// Compornents
import WebviewRedrawProgress from '@/components/Molecules/WebviewRedrawProgress'
import LeafletMap from '@/components/Molecules/LeafletMap'
import CommonDateHeader from '@/components/Molecules/CommonDateHeader'
import BarChart from '@/components/Molecules/BarChart'

// ライブラリ
import Swiper from 'swiper'
import moment from 'moment'
moment.locale('ja')

export default {
	name: 'PetWalkingReport',
	components: {
		WebviewRedrawProgress,
		LeafletMap,
		CommonDateHeader,
		BarChart
	},
	props: {
		petId: {
			type: String,
			required: true
		}
	},
	data () {
		return {
			isLoading: false,
			isMapLoading: false,
			isVisibleModal: false,
			displayType: 'day',
			modalType: 'walking',
			summaryData: null,
			summaryMonthlyData: null,
			logData: [],
			logDateList: [],
			logDate: moment().hours(0).minute(0).second(0).millisecond(0).unix(),
			targetDate: moment().hours(0).minute(0).second(0).millisecond(0).unix(),
			swiper: null,
			selectedMonth: '',
			monthList: []
		}
	},
	created () {
		// URLパラメータから日付を取得、なければデフォルト値
		if (this.$route.query.date) {
			this.targetDate = moment.unix(parseInt(this.$route.query.date)).hours(0).minute(0).second(0).millisecond(0).unix()
			this.logDate = this.targetDate
		}
		// console.log('logDate', this.logDate)
		// console.log('petId', this.petId)
	},
	mounted () {
	},
	computed: {
		// map Vuex getters
		...mapGetters('common', ['userId', 'userToken']),
		...mapGetters('pet', ['petWalkingSummaryData', 'petWalkingHistoryData']),

		// サマリーデータ
		computedSummaryData () {
			// 初期化
			const summary = {
				average_distance: 0,
				average_duration: 0,
				total_distance: 0,
				total_duration: 0
			}

			// データ整形
			if (this.summaryData) {
				summary.average_distance = this.convertRoundData(this.summaryData.average_distance, 'distance')
				summary.average_duration = this.convertRoundData(this.summaryData.average_duration, 'duration')
				summary.total_distance = this.convertRoundData(this.summaryData.total_distance, 'distance')
				summary.total_duration = this.convertRoundData(this.summaryData.total_duration, 'duration')
			}
			return summary
		},

		// 年表示時の月サマリーデータ
		computedSummaryMonthlyData () {
			// 初期化
			const summary = {
				average_distance: 0,
				average_duration: 0,
				total_distance: 0,
				total_duration: 0
			}

			// データ整形
			if (this.summaryMonthlyData) {
				summary.average_distance = this.convertRoundData(this.summaryMonthlyData.average_distance, 'distance')
				summary.average_duration = this.convertRoundData(this.summaryMonthlyData.average_duration, 'duration')
				summary.total_distance = this.convertRoundData(this.summaryMonthlyData.total_distance, 'distance')
				summary.total_duration = this.convertRoundData(this.summaryMonthlyData.total_duration, 'duration')
			}
			return summary
		},

		// チャートデータ
		computedChartData () {
			// 初期化
			const key = Math.round(Math.random() * 1000)	// ランダムなキーを設定
			const chart = {
				distance_labels: [],
				distance_data: [],
				distance_key: key,
				duration_labels: [],
				duration_data: [],
				duration_key: key + 1	// keyが被らないように+1
			}

			// データを表示しやすいように加工するメソッド（formatは日='H'、月='M'、曜日='d'）
			const getRecordsData = (array, format) => {
				const data = {}
				for (let i = 0, len = array.length; i < len; i++) {
					const item = array[i]
					const key = moment.unix(item.date).format(format)
					if (data[key]) {	// NOTE: すでにデータがある場合は加算
						data[key] = {
							distance: data[key].distance + item.distance,
							duration: data[key].duration + item.duration
						}
					} else {
						data[key] = {
							distance: item.distance,
							duration: item.duration
						}
					}
				}
				return data
			}

			// データ整形（this.summaryDataがなくても表示する）
			if (this.displayType === 'day') {	// 日表示
				// NOTE: 日表示の場合は散歩履歴データを使用
				const records = this.logData.length > 0 ? this.logData : []
				const recordsData = getRecordsData(records, 'H')

				for (let i = 0; i < 24; i++) {
					chart.distance_labels.push(i)
					chart.duration_labels.push(i)
					chart.distance_data.push(recordsData[`${i}`] ? this.convertRoundData(recordsData[`${i}`].distance, 'distance') : 0)
					chart.duration_data.push(recordsData[`${i}`] ? this.convertRoundData(recordsData[`${i}`].duration, 'duration') : 0)
				}
			} else {	// 週、月、年表示
				// NOTE: 週、月、年表示の場合はサマリーデータを使用
				const records = this.summaryData ? this.summaryData.records : []

				const len = this.displayType === 'week' ? 7	// 週の場合は7日
					: this.displayType === 'month' ? records.length > 0 ? records.length : 28	// 月の場合はrecordsの長さ、recordsがなければ月の最小日数の28日
						: 12	// 年の場合は12ヶ月
				for (let i = 0; i < len; i++) {
					const record = records[i]
					chart.distance_labels.push(i)
					chart.duration_labels.push(i)
					chart.distance_data.push(record ? this.convertRoundData(record.distance, 'distance') : 0)
					chart.duration_data.push(record ? this.convertRoundData(record.duration, 'duration') : 0)
				}
			}
			return chart
		},

		// 散歩ログデータ
		computedlogData () {
			// 初期化
			const log = []

			// データ整形
			if (this.logData.length > 0) {
				for (let i = this.logData.length - 1; i >= 0; i--) {
					const item = this.logData[i]
					const objDate = moment.unix(item.date)
					const date = objDate.format('YYYY年MM月DD日')
					const start = objDate.format('HH時mm分')
					const end = objDate.add(item.duration, 's').format('HH時mm分')
					const distance = this.convertRoundData(item.distance, 'distance')
					const elapsedMinutes = this.convertRoundData(item.duration, 'duration')
					const coordinate = this.convertCoordinates(item.paths)
					const key = Math.round(Math.random() * 1000)	// ランダムなキーを設定
					log.push({
						date: date,
						coordinate: coordinate,
						mapErrorMessage: coordinate.length === 0 ? '手入力' : '',	// データがあり経路緯度経度がない場合は「手入力」とする
						duration: { start: start, end: end },
						distance: distance,
						elapsedMinutes: elapsedMinutes,
						key: key
					})
				}
			}
			return log
		}
	},
	methods: {
		// map Vuex actions
		...mapActions('pet', [SET_PET_WALKING_SUMMARY_DATA, SET_PET_WALKING_HISTORY_DATA]),

		// 初期化処理
		init (value) {
			// console.log('emmit value: ', value)
			this.displayType = value.type

			// from, toの設定
			const from = value.from
			const to = value.to
			// console.log('from: ', from)
			// console.log('to: ', to)

			// サマリーデータ取得
			this.getSummaryData(from, to, moment.unix(value.to).year())

			// 散歩データを取得
			this.getLogData(from)
		},

		// サマリーデータ取得API
		getSummaryData (from, to, year) {
			this.isLoading = true

			// 年とそれ以外の表示でリクエスト内容を変える
			const request = {
				from: from,
				to: to,
				year: year
			}
			if (this.displayType === 'year') {
				request.from = null
				request.to = null
			} else {
				request.year = null
			}

			// リクエスト
			this.SET_PET_WALKING_SUMMARY_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				pet_id: this.petId,
				from_date: request.from,
				to_date: request.to,
				year: request.year
			}).then(res => {
				// console.log('petWalkingSummaryData: ', this.petWalkingSummaryData)
				// サマリーデータ設定
				if (this.petWalkingSummaryData.records.length > 0) {
					this.summaryData = this.petWalkingSummaryData
				} else {
					this.summaryData = null	// 1件もデータがない場合はnullを設定
				}
			}).catch(error => {
				console.log('getSummaryData Error: ', error)
				// エラーの場合はnullを設定
				this.summaryData = null
			}).finally(() => {
				// 年表示時の月サマリーデータ取得APIを実行
				if (this.displayType === 'year') {
					this.selectedMonth = from	// 選択している月を1月に初期化
					// console.log('this.selectedMonth', this.selectedMonth)
					this.getSummaryMonthlyData()
				} else {
					// 年表示以外は日付UIの初期化（年表示はgetSummaryMonthlyDataで行う）
					this.initDateUi(from, to)
				}
				this.isLoading = false
			})
		},

		// 年表示時の月サマリーデータ取得API
		getSummaryMonthlyData () {
			this.isLoading = true
			const from = moment.unix(this.selectedMonth).startOf('month').unix()
			const to = moment.unix(this.selectedMonth).endOf('month').unix()

			// リクエスト
			this.SET_PET_WALKING_SUMMARY_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				pet_id: this.petId,
				from_date: from,
				to_date: to,
				year: null
			}).then(res => {
				// console.log('getSummaryMonthlyData: ', this.petWalkingSummaryData)
				if (this.petWalkingSummaryData.records.length > 0) {
					this.summaryMonthlyData = this.petWalkingSummaryData
				} else {
					this.summaryMonthlyData = null	// 1件もデータがない場合はnullを設定
				}
			}).catch(error => {
				console.log('getSummaryMonthlyData Error: ', error)
				// エラーの場合はnullを設定
				this.summaryMonthlyData = null
			}).finally(() => {
				// 日付UIを選択した月の日に初期化
				this.initDateUi(from, to)
				this.isLoading = false
			})
		},

		// 散歩データ取得API
		getLogData (date) {
			this.isMapLoading = true

			this.SET_PET_WALKING_HISTORY_DATA({
				user_id: this.userId,
				user_token: this.userToken,
				pet_id: this.petId,
				date: date
			}).then(res => {
				// console.log('petWalkingHistoryData: ', this.petWalkingHistoryData)
				if (this.petWalkingHistoryData.histories.length > 0) {
					this.logData = this.petWalkingHistoryData.histories
				} else {
					this.logData = []	// 1件もデータがない場合は空配列を設定
				}
			}).catch(error => {
				// NOTE: エラーの場合は空配列を設定
				console.log('getLogData Error: ', error)
				this.logData = []
			}).finally(() => {
				this.isMapLoading = false
			})
		},

		// PetVoiceの座標データをLeaflet用に変換するメソッド
		convertCoordinates (paths) {
			const coordinate = []
			const arrayPaths = paths ? paths.split('|') : []	// NOTE: パスがない場合は空配列を返す
			for (let i = 0, len = arrayPaths.length; i < len; i++) {
				const arrayPath = arrayPaths[i].split(',')
				coordinate.push([
					Number(arrayPath[0]),
					Number(arrayPath[1])
				])
			}
			return coordinate
		},

		// 距離と経過時間を整形するメソッド（距離は小数点第2位、経過時間は小数点第1位で表示）
		convertRoundData (value, type) {
			let data = 0
			if (type === 'distance') {
				data = Math.round((value / 1000) * 100) / 100
			} else {
				data = Math.round((value / 60) * 10) / 10
			}
			return data
		},

		// 日付を選択したときの処理
		setlogDate (value) {
			// console.log(moment.unix(value).format('YYYY-MM-DD HH:mm:ss'))
			// 選択している日を更新し、散歩ログデータ取得
			this.logDate = value
			this.getLogData(value)
		},

		// 月を変更した際の処理
		changeMonth () {
			// その月の最初の日の散歩ログデータ取得
			this.getLogData(this.selectedMonth)

			// 選択した月のサマリーデータ取得
			this.getSummaryMonthlyData()
		},

		// Swiperの処理
		initSwiper () {
			this.swiper = new Swiper('#js_swiper', {
				slidesPerView: 'auto',
				initialSlide: 0
			})
		},

		// 日付変更UIの初期化処理
		initDateUi (from, to) {
			// 初期化
			this.logDateList = []
			this.monthList = []
			this.logDate = from
			let elapsedDate = 0
			let startDay = null
			let summary = null
			let isEmptyList = []

			// fromのみmomentオブジェクトに変換
			const objFrom = moment.unix(from)
			// console.log(objFrom.format('YYYY-MM-DD HH:mm:ss'))
			// console.log(moment.unix(to).format('YYYY-MM-DD HH:mm:ss'))

			// 年の場合は月のセレクトボックスを作成
			if (this.displayType === 'year') {
				const targetMonth = objFrom.startOf('month')

				// 開始日～終了日と月の日数を取得
				startDay = targetMonth.clone().startOf('month')
				const lastDay = targetMonth.clone().endOf('month')
				elapsedDate = lastDay.diff(startDay, 'days')

				// 月のセレクトボックス作成
				let startMonth = targetMonth.clone().startOf('year')
				for (let i = 0, len = 12; i < len; i++) {
					this.monthList.push({
						text: startMonth.format('YYYY年MM月'),
						value: startMonth.unix()
					})
					startMonth = startMonth.add(1, 'months')
				}

				// データがない月のデータを作成
				summary = this.summaryMonthlyData
			} else {
				// 開始日と月の日数を取得
				const objTo = moment.unix(to)
				elapsedDate = objTo.diff(objFrom, 'days')
				startDay = objFrom.clone()

				// データがない月のデータを作成
				summary = this.summaryData
			}

			// データを持たない日付フラグリストを作成
			if (summary) {
				isEmptyList = summary.records.map(item => item.duration === 0)
			} else {
				isEmptyList = Array.from({ length: elapsedDate + 1 }, () => true)
			}
			// console.log('isEmptyList', isEmptyList)

			// 日付変更UIの作成
			for (let i = 0, len = elapsedDate + 1; i < len; i++) {
				this.logDateList.push({
					date: startDay.format('D'),
					weekday: startDay.format('ddd'),
					is_empty: isEmptyList[i],
					value: startDay.unix()
				})
				startDay.add(1, 'days')
			}

			// Swiperの初期化およびアップデート
			this.$nextTick(() => {
				setTimeout(() => {
					if (this.swiper) {
						this.swiper.update()
						this.swiper.slideTo(0)
					} else {
						this.initSwiper()
					}
				}, 100)
			})
		},

		// モーダルを表示するメソッド
		openModal (type) {
			this.modalType = type
			this.isVisibleModal = true
		}
	}
}
</script>

<style lang="scss" scoped>
@import '~swiper/css/swiper.min.css';
@import "../../assets/sass/variable";

// NOTE: デザインシステム外のため他での利用禁止
$color-pet-walking-primary: #23B3D2;

$spacing-1: 0.0625rem;
$spacing-5: 0.3125rem;
$spacing-9: 0.5625rem;
$spacing-24: 1.5rem;
$spacing-32: 2rem;
$spacing-40: 2.5rem;

$font-size-22: 1.375rem;
$font-size-32: 2rem;

$color-date: #666;
$color-date-disabled: #999;

.pet-walking {
	position: relative;
	width: 100%;
	font-family: $lp-family-primary;
	color: $text-primary;

	// mplusのジャギー対策
	p, span, em, a, h2, h3, option { transform: rotate(0.05deg); }

	&__title {
		margin: 0;
		padding: 0;
		background-color: $background-secondary;
	}

	&__summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin: 0;
		padding: $spacing-8 $spacing-20;
		background-color: $background-secondary;

		&--title {
			margin: 0;
			padding: 0;
			font-family: $family-primary;
			font-size: $font-size-16;
			font-weight: normal;
		}

		&--contents {
			display: flex;
			justify-content: space-between;
			flex-direction: column;
			flex-grow: 1;
			width: 34.4%;
			box-sizing: border-box;

			&:first-of-type {
				padding: 0 0 0 4.2666%;
			}

			&:last-of-type {
				padding: 0 0 0 4%;
				border-left: 1px solid $line-primary;
			}

			h3 {
				margin: 0;
				padding: 0;
				font-family: $lp-family-primary;
				font-size: $font-size-12;
				font-weight: normal;
				line-height: $line-height-1;
			}

			p {
				margin: 0;
				padding: $spacing-4 0 0;
				font-family: $family-primary;
				font-size: $font-size-14;
				color: $color-pet-walking-primary;

				& > em {
					margin: 0 $spacing-4;
					font-size: $font-size-32;
					font-style: normal;
					line-height: $line-height-1;
				}
			}
		}
	}

	&__inner {
		max-width: 100%;
		padding: 0 0 $spacing-16;
	}

	&__subtitle {
		margin: 0;
		padding: 0;
		font-family: $family-primary;
		font-size: $font-size-16;
		font-weight: normal;
	}

	&__contents {
		position: relative;
		width: 100%;
		margin: $spacing-24 auto 0;
		padding: 0 $spacing-20;
		background-color: $background-primary;
		box-sizing: border-box;
	}

	// 月変更UI
	&__month {
		margin: $spacing-40 auto 0;

		&--select {
			// width: 110px;
			margin-left: $spacing-16;
			padding: 0;
			font-family: $family-primary;
			font-size: $font-size-16;
			color: $text-primary;
			background-color: $background-primary;
			border: none;

			option { font-size: $font-size-12; }
		}

		.pet-walking__summary { margin-top: $spacing-8; }

		& + .pet-walking__contents { margin-top: $spacing-24; }
	}

	// 日付変更UI
	&__select-date {
		width: 100%;
		margin: $spacing-8 auto 0;

		&--inner {
			width: 100%;
			list-style: none;
			padding: 0;
			margin: 0;
		}

		&--item {
			display: flex;
			justify-content: center;
			flex-direction: column;
			width: $spacing-40;
			height: $spacing-40;
			margin: 0;
			border-radius: $spacing-40;
			border: $spacing-1 solid $primary;
			box-sizing: border-box;
			color: $color-date;
			cursor: pointer;

			& > p {
				margin: 0;
				text-align: center;

				&.is-date {
					font-family: $family-primary;
					font-size: $font-size-12;
				}

				&.is-weekday {
					font-family: $lp-family-primary;
					font-size: $font-size-10;
				}
			}

			&.is-select {
				background-color: $primary;
				color: $text-seconday !important;
				pointer-events: none;
			}

			&.is-empty {
				border: $spacing-1 solid $color-date-disabled;
				color: $color-date-disabled;
			}
		}

		&--item + &--item { margin-left: $spacing-9; }
	}

	// ローディング
	&__loading {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		background: rgba($_black, 0.4);
		overflow: hidden;
		z-index: 9999;

		.redraw-progress {
			position: fixed;
			background: none;
			height: 100vh;
		}
	}

	// Map
	&__map {
		margin: $spacing-8 0 0;
		border-radius: $spacing-8;
		overflow: hidden;
		border: 1px solid $line-primary;
		box-sizing: border-box;

		&--wrapper { position: relative; }

		&--summary {
			display: flex;
			justify-content: flex-start;
			padding: $spacing-8;

			section {
				padding: 0 $spacing-16;

				&:first-of-type { border-right: 1px solid $line-primary; }
			}

			h3 {
				margin: 0;
				font-family: $family-primary;
				font-size: $font-size-10;
				font-weight: normal;
			}

			p {
				margin: 0;
				font-family: $family-primary;
				font-size: $font-size-14;
			}

			.is-duration p,
			.is-duration h3 {
				font-size: $font-size-12;
			}
		}

		&--error {
			margin-top: $spacing-8;
			padding: $spacing-8;
			border-radius: $spacing-4;
			border: $spacing-1 solid $line-primary;

			& > p {
				margin: 0;
				text-align: center;
				font-size: $font-size-12;
			}
		}
	}

	// データ精度について
	&__data-quality {
		margin: $spacing-32 auto 0;
		font-size: $font-size-12;
		text-align: center;
		text-decoration: underline;
	}

	// モーダル
	&__modal {
		position: fixed;
		width: 100vw;
		height: 100vh;
		top: 0;
		left: 0;
		background: rgba($_black, .5);
		overflow: hidden;
		z-index: 9999;

		&--inner {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
			width: 94.6667%;
			box-sizing: border-box;
			background-color: $background-primary;
			border-radius: $spacing-10;
		}

		&--title {
			position: relative;
			margin: 0;
			padding: $spacing-8;
			background-color: $primary;
			font-family: $family-primary;
			font-weight: normal;
			font-size: $font-size-18;
			color: $text-seconday;
			text-align: center;
			border-radius: $spacing-10 $spacing-10 0 0;

			svg {
				display: block;
				position: absolute;
				width: 40px;
				top: 50%;
				left: $spacing-8;
				transform: translateY(-50%);
				z-index: 1;
			}

			span {
				display: block;
				flex: 1;
				text-align: center;
			}
		}

		&--body {
			padding: $spacing-20;
			font-size: $font-size-14;

			& > p { margin: 0; }
		}
	}
}

@media (max-width: (370px)) {
	.pet-walking {
		&__summary {
			padding: $spacing-8 $spacing-16;

			&--title { font-size: $font-size-14; }

			&--contents {
				h3 { font-size: $font-size-10; }

				p {
					font-size: $font-size-12;

					& > em { font-size: $font-size-24; }
				}
			}
		}

		&__inner {
			max-width: 100%;
			padding: 0 0 $spacing-16;
		}

		&__subtitle { font-size: $font-size-14; }

		&__contents { padding: 0 $spacing-16; }

		&__month--select {
			font-size: $font-size-14;

			option { font-size: $font-size-10; }
		}

		&__map--summary {
			padding: $spacing-6;

			section { padding: 0 $spacing-14; }

			h3 { font-size: $font-size-8; }

			p { font-size: $font-size-12; }

			.is-duration p,
			.is-duration h3 {
				font-size: $font-size-10;
			}
		}
	}
}

// アニメーション
.fade-enter-active, .fade-leave-active {
	transition: opacity .2s;
}
.fade-enter, .fade-leave-to {
	opacity: 0;
}
</style>
